import { useMemo, useEffect } from 'react'
import '../styles/Legend.css'
import { countryLabel } from '../utils/countryLabel'
import { STAT_RAMPS, STAT_NEUTRAL } from '../utils/colors'
import { OWN_LAYERS, resolveOwnLayer } from '../utils/regionStats'
import { WEATHER_COLORS, SYSTEM_COLORS } from '../utils/weather'
import { useMapStore } from '../store/mapStore'
import { usePrefsStore } from '../store/prefsStore'
import { PALETTE_LABELS, resolvePalette, samplePalette } from '../utils/palettes'
import { CLASS_METHODS, computeBreaks } from '../utils/classify'
import { CONTINUOUS_LAYERS, NO_DATA_COLOR, activeContinuousKey } from '../utils/choropleth'
import { ACTIVITY_COLORS } from '../utils/recentActivityData'
import { DIPLOMACY_COLORS } from '../utils/diplomacyData'

const HEMISPHERE_VALUE = { 'Hémisphère Nord': 'north', 'Hémisphère Sud': 'south', 'Équateur': 'equator' }

// Build low→high legend items from data-driven class breaks + the active
// palette. Metric, breaks and no-data rule all come from `choropleth.js`, the
// same definition the map paints from.
const buildContinuousLegend = (def, features, palette, customRamp, classification, nClasses) => {
  const colors = samplePalette(resolvePalette(palette, customRamp), nClasses)
  const breaks = computeBreaks((features || []).map((f) => def.value(f.properties)), classification, nClasses)
  const items = colors.map((color, i) => {
    if (i === 0) return { color, label: `< ${def.fmt(breaks[0] ?? 0)}` }
    if (i === colors.length - 1) return { color, label: `≥ ${def.fmt(breaks[breaks.length - 1] ?? 0)}` }
    return { color, label: `${def.fmt(breaks[i - 1])} – ${def.fmt(breaks[i])}` }
  })
  if (def.nodata) items.push({ color: NO_DATA_COLOR, label: 'Aucune donnée' })
  const method = CLASS_METHODS.find((item) => item.key === classification)?.label || classification
  return {
    title: def.title,
    detail: `${PALETTE_LABELS[palette] || palette} · ${method} · ${nClasses} classes`,
    items,
  }
}

// Matches the map's weather-systems-fill paint (fill-opacity interpolates
// 0.05..0.28 by intensity) — use a representative mid-intensity value so the
// legend swatch reads the same as an average circle on the map.
const hexToRgba = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16)
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const Legend = ({ layers, regionsData, statType, structFilter, selectedOrgId, organizationsCatalog, diplomacyData }) => {
  const palette = usePrefsStore((s) => s.palette)
  const classification = usePrefsStore((s) => s.classification)
  const nClasses = usePrefsStore((s) => s.nClasses)
  const customRamp = usePrefsStore((s) => s.customRamp)

  // Calculate legend items based on active layer
  const legendItems = useMemo(() => {
    if (!regionsData) return null

    // Owned-country heatmap layers — build the scale straight from the ramp.
    const ownLayer = resolveOwnLayer(layers, statType)
    if (ownLayer) {
      const def = OWN_LAYERS.find((l) => l.key === ownLayer.key)
      const ramp = STAT_RAMPS[def.ramp] || []
      let selected = def.dynamic ? statType?.[def.dynamic] : null
      if (def.structFilter) {
        const parts = [
          structFilter?.type || 'Toutes structures',
          structFilter?.specialisation && structFilter.specialisation !== 'NA' ? structFilter.specialisation : null,
          structFilter?.level ? `Nv.${structFilter.level}` : null,
        ].filter(Boolean)
        selected = parts.join(' ')
      }
      const unit = def.ramp === 'infrastructure' ? ' km' : (def.ramp.includes('coverage') || def.ramp.includes('meeting') ? ' %' : '')
      return {
        title: selected ? `${def.label.replace(/\s*\(.*\)$/, '')} — ${selected}` : def.label,
        items: [
          ...ramp.map(([stop, color]) => ({ color, label: `≥ ${stop.toLocaleString('fr-FR')}${unit}` })),
          { color: STAT_NEUTRAL, label: 'Aucune donnée / autre pays' },
        ],
      }
    }

    // Don't show legend if no specific layer is active (neutral gray mode)
    const noLayerActive = !layers.countries && !layers.population && !layers.populationDensity && !layers.gdpPerCapita && !layers.climate && !layers.weather && !layers.recentActivity && !layers.diplomacy && !layers.hemisphere && !layers.playedCountries && !layers.organizations && !layers.topography
    if (noLayerActive) return null

    // Countries layer: Count unique countries - if more than 10, don't show legend
    if (layers.countries) {
      const uniqueCountries = new Set()
      regionsData.features.forEach(feature => {
        if (feature.properties.country_id) {
          uniqueCountries.add(feature.properties.country_id)
        }
      })
      
      if (uniqueCountries.size > 10) return {
        title: 'Contrôle territorial',
        detail: 'Chaque pays utilise sa couleur déclarée',
        items: [
          { color: '#888888', label: 'Régions non assignées' },
          { color: 'linear-gradient(135deg, #3b82f6, #d5b654, #dc5050)', label: 'Territoires des pays' },
        ],
      }
      
      // Build country legend
      const countryMap = new Map()
      regionsData.features.forEach(feature => {
        const { country_id, country_name, country_color } = feature.properties
        if (country_id && !countryMap.has(country_id)) {
          countryMap.set(country_id, { name: country_name, color: country_color })
        }
      })
      
      const items = [
        { color: '#888888', label: 'Régions non assignées' },
        ...Array.from(countryMap.entries()).map(([id, c]) => ({ color: c.color, label: countryLabel(c.name, id), iso: { prop: 'country_id', value: id } }))
      ]

      return { title: 'Contrôle territorial', items }
    }

    // Played Countries layer
    if (layers.playedCountries) {
      return {
        title: 'Pays joués',
        items: [
          { color: '#888888', label: 'Régions sans pays' },
          { color: '#4CAF50', label: 'Pays non joués' },
          { color: '#E24A4A', label: 'Pays joués (actifs)' },
          { color: '#FFA500', label: 'Actuellement Inactif (>5 mois)' }
        ]
      }
    }

    if (layers.diplomacy) return {
      title: 'Relations avec mon pays',
      detail: diplomacyData?.country_id ? 'Traités publics actifs et relations officielles' : 'Connexion requise',
      items: [
        { color: DIPLOMACY_COLORS.self, label: 'Mon pays' },
        { color: DIPLOMACY_COLORS.ally, label: 'Allié' },
        { color: DIPLOMACY_COLORS.treaty, label: 'Traité public actif' },
        { color: DIPLOMACY_COLORS.enemy, label: 'En guerre / ennemi' },
        { color: DIPLOMACY_COLORS.neutral, label: 'Neutre' },
        { color: DIPLOMACY_COLORS.unplayed, label: 'Pays non joué' },
        { color: DIPLOMACY_COLORS.unclaimed, label: 'Région non attribuée' },
      ],
    }

    // Continuous layers — data-driven breaks + active palette (S11/S12).
    const continuousKey = activeContinuousKey(layers)
    if (continuousKey) {
      return buildContinuousLegend(CONTINUOUS_LAYERS[continuousKey], regionsData.features, palette, customRamp, classification, nClasses)
    }

    // Climate layer
    if (layers.climate) {
      return {
        title: 'Zones climatiques',
        items: [
          { color: '#A9E55F', label: 'Tempéré Hiver sec' },
          { color: '#C6483F', label: 'Tempéré Été sec' },
          { color: '#95B881', label: 'Tempéré Humide' },
          { color: '#89C342', label: 'Tropical Humide et Sec' },
          { color: '#378E2A', label: 'Tropical Humide' },
          { color: '#E8BD68', label: 'Aride' },
          { color: '#FEFFB3', label: 'Désertique' },
          { color: '#B89D67', label: 'Continental Sec' },
          { color: '#D3E5C9', label: 'Continental Humide' },
          { color: '#93408D', label: 'Montagne' },
          { color: '#6593DC', label: 'Arctique' }
        ].map((it) => ({ ...it, iso: { prop: 'climate', value: it.label } }))
      }
    }

    // Live weather layer
    if (layers.weather) {
      return {
        title: 'Météo en direct',
        items: [
          ...Object.entries(WEATHER_COLORS).map(([label, color]) => ({ color, label, iso: { prop: 'weather_type', value: label } })),
          { color: '#3a3a3a', label: 'Aucune donnée' },
          {
            circle: true,
            color: hexToRgba(SYSTEM_COLORS.depression, 0.22),
            borderColor: hexToRgba(SYSTEM_COLORS.depression, 0.7),
            label: 'Dépression',
          },
          {
            circle: true,
            color: hexToRgba(SYSTEM_COLORS.anticyclone, 0.22),
            borderColor: hexToRgba(SYSTEM_COLORS.anticyclone, 0.7),
            label: 'Anticyclone',
          },
        ],
      }
    }

    if (layers.recentActivity) return {
      title: 'Activité publique — 48 h',
      detail: 'Le halo pulse à la position de l’action',
      items: [
        { color: ACTIVITY_COLORS.construction, label: 'Construction' },
        { color: ACTIVITY_COLORS.country, label: 'Territoire' },
        { color: ACTIVITY_COLORS.military, label: 'Militaire' },
      ],
    }

    // Hemisphere layer
    if (layers.hemisphere) {
      return {
        title: 'Hémisphères',
        items: [
          { color: '#4A90E2', label: 'Hémisphère Nord' },
          { color: '#E24A4A', label: 'Hémisphère Sud' },
          { color: '#D5B654', label: 'Équateur' }
        ].map((it) => ({ ...it, iso: { prop: 'hemisphere', value: HEMISPHERE_VALUE[it.label] } }))
      }
    }

    // Organizations layer
    if (layers.organizations) {
      const orgColor = (organizationsCatalog && selectedOrgId != null && organizationsCatalog[selectedOrgId]?.color) || '#4A90E2'
      const orgName = (organizationsCatalog && selectedOrgId != null && organizationsCatalog[selectedOrgId]?.name) || 'Organisation sélectionnée'
      return {
        title: `Organisation — ${orgName}`,
        items: [
          { color: orgColor, label: 'Membre' },
          { color: '#3a3a3a', label: 'Non membre' }
        ]
      }
    }

    // Topography layer
    if (layers.topography) {
      return {
        title: 'Topographie',
        items: [
          { color: '#cccccc', label: 'Régions (transparent)' },
          { color: '#8B7355', label: 'Terrain (couche raster)' }
        ]
      }
    }

    return null
  }, [layers, regionsData, statType, structFilter, selectedOrgId, organizationsCatalog, diplomacyData, palette, classification, nClasses, customRamp])

  const legendIsolate = useMapStore((s) => s.legendIsolate)
  const setLegendIsolate = useMapStore((s) => s.setLegendIsolate)

  // Reset the isolation whenever the active layer (legend title) changes, so a
  // stale filter never carries over to a different thematic layer.
  const title = legendItems?.title
  useEffect(() => {
    setLegendIsolate(null)
    return () => setLegendIsolate(null)
  }, [title, setLegendIsolate])

  if (!legendItems) return null

  const isActive = (iso) => legendIsolate && iso && legendIsolate.prop === iso.prop && legendIsolate.value === iso.value
  const anyActive = !!legendIsolate
  const onItemClick = (iso) => {
    if (!iso) return
    setLegendIsolate(isActive(iso) ? null : { prop: iso.prop, value: iso.value })
  }

  return (
    <aside className="legend pr-widget" aria-label={`Légende — ${legendItems.title}`}>
      <h3 className="legend-title">{legendItems.title}</h3>
      {legendItems.detail && <p className="legend-detail">{legendItems.detail}</p>}
      <div className="legend-items">
        {legendItems.items.map((item, index) => {
          const clickable = !!item.iso
          const active = isActive(item.iso)
          const dimmed = anyActive && clickable && !active
          const ItemTag = clickable ? 'button' : 'div'
          return (
            <ItemTag
              key={index}
              type={clickable ? 'button' : undefined}
              className={`legend-item${clickable ? ' clickable' : ''}${active ? ' active' : ''}${dimmed ? ' dimmed' : ''}`}
              onClick={() => onItemClick(item.iso)}
              title={clickable ? 'Cliquer pour isoler cette catégorie' : undefined}
              aria-pressed={clickable ? active : undefined}
            >
              <div
                className={item.circle ? 'legend-color legend-color-circle' : 'legend-color'}
                style={{
                  background: item.color,
                  ...(item.borderColor ? { borderColor: item.borderColor } : {}),
                }}
                aria-hidden="true"
              />
              <span className="legend-label">{item.label}</span>
            </ItemTag>
          )
        })}
      </div>
    </aside>
  )
}

export default Legend
